import { Capacitor } from "@capacitor/core";

type FailureSource = "render" | "javascript" | "promise" | "native-bridge";
const sent = new Set<string>();
const errorNames = new Set(["Error", "TypeError", "RangeError", "ReferenceError", "SyntaxError", "URIError"]);

// Error messages often contain URLs, OTPs or user content. Never forward them.
// Keep only code locations in our own hashed assets, without query parameters.
export function diagnosticFrames(error: unknown): { fileName: string; lineNumber: number }[] {
  if (!(error instanceof Error) || !error.stack) return [];
  return [...error.stack.matchAll(/(?:https:\/\/[^\s/]+)?(\/assets\/[A-Za-z0-9_.-]+\.js):([0-9]+):[0-9]+/g)]
    .slice(0, 12).map(match => ({ fileName: match[1], lineNumber: Number(match[2]) }));
}

export async function recordNativeFailure(source: FailureSource, error?: unknown): Promise<void> {
  if (!Capacitor.isNativePlatform() || !Capacitor.isPluginAvailable("FirebaseCrashlytics")) return;
  const kind = error instanceof Error && errorNames.has(error.name) ? error.name : "Error";
  const frames = diagnosticFrames(error);
  const key = `${source}:${kind}:${frames[0]?.fileName ?? ""}:${frames[0]?.lineNumber ?? 0}`;
  if (sent.has(key) || sent.size >= 20) return;
  sent.add(key);
  try {
    const { FirebaseCrashlytics } = await import("@capacitor-firebase/crashlytics");
    await FirebaseCrashlytics.recordException({ message: `Lift & Learn ${source}: ${kind}`, stacktrace: frames });
  } catch { /* Diagnostics must never prevent recovery or create a rejection loop. */ }
}

export function watchNativeDiagnostics(): () => void {
  if (!Capacitor.isNativePlatform() || !Capacitor.isPluginAvailable("FirebaseCrashlytics")) return () => {};
  const error = (event: ErrorEvent) => { void recordNativeFailure("javascript", event.error); };
  const rejection = (event: PromiseRejectionEvent) => { void recordNativeFailure("promise", event.reason); };
  window.addEventListener("error", error);
  window.addEventListener("unhandledrejection", rejection);
  return () => {
    window.removeEventListener("error", error);
    window.removeEventListener("unhandledrejection", rejection);
  };
}
